import { useState, useEffect } from "react";
import { Wallet, Save, X, MessageCircle, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";
import { buildReceiptMessage, openWaForReceipt } from "@/lib/whatsapp";

const METHODS = [
  { v: "bank_transfer", l: "Bank transfer" },
  { v: "cash", l: "Cash" },
  { v: "card", l: "Card" },
  { v: "cheque", l: "Cheque" },
  { v: "other", l: "Other" },
];

/**
 * Modal to record a payment (deposit or final) against an invoice.
 *
 * After the payment is recorded, the dialog swaps to a "Receipt" view
 * that lets the user WhatsApp the client (with amount received, remaining
 * balance, due date, and a public invoice PDF link) or copy the text to
 * paste into any other channel.
 */
export function PaymentDialog({
  open, onOpenChange, invoice, onSaved,
}) {
  const { lang } = useI18n();
  const balance = Number(invoice?.balance_due ?? invoice?.total ?? 0);
  const currency = invoice?.currency || "USD";

  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [paidAt, setPaidAt] = useState("");
  const [autoEmail, setAutoEmail] = useState(true);
  const [busy, setBusy] = useState(false);

  // Post-save "Receipt ready" state
  const [receipt, setReceipt] = useState(null);
  const [receiptLang, setReceiptLang] = useState("en");

  useEffect(() => {
    if (!open) return;
    if (!invoice) return;
    setAmount(balance > 0 ? balance.toFixed(2) : "");
    setMethod("bank_transfer");
    setReference("");
    setNotes("");
    setPaidAt(new Date().toISOString().slice(0, 10));
    setAutoEmail(true);
    setReceipt(null);
    setReceiptLang(lang === "tt" ? "tt" : "en");
    // Only reset on the open false→true transition — depending on `invoice`
    // or `balance` would re-run the reset every time the parent refetches
    // the invoice (which happens right after paymentMut.onSuccess), which
    // would auto-dismiss the receipt-ready sub-view.
  }, [open]);

  const numericAmount = Number(amount || 0);
  const willSettle = numericAmount >= balance - 0.005 && balance > 0;
  const remaining = Math.max(0, balance - numericAmount);
  const isPartial = numericAmount > 0 && !willSettle;

  async function submit() {
    if (numericAmount <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }
    if (numericAmount > balance + 0.01) {
      toast.error(`Amount exceeds outstanding balance ${balance.toFixed(2)} ${currency}`);
      return;
    }
    setBusy(true);
    try {
      const body = {
        amount: numericAmount,
        method,
        reference: reference.trim(),
        notes: notes.trim(),
        paid_at: paidAt ? new Date(paidAt + "T00:00:00Z").toISOString() : undefined,
        auto_email: willSettle ? autoEmail : false,
      };
      const res = await onSaved(body);
      toast.success(
        willSettle
          ? (res?.auto_emailed ? "Invoice marked paid · receipt emailed" : "Invoice marked paid")
          : `Payment of ${numericAmount.toFixed(2)} ${currency} recorded`,
      );
      // Show the receipt-ready sub-view
      if (res?.receipt) {
        setReceipt(res.receipt);
      } else {
        onOpenChange(false);
      }
    } catch (e) {
      const d = e?.response?.data?.detail || "Failed to record payment";
      toast.error(d);
    } finally {
      setBusy(false);
    }
  }

  // ─── Post-save receipt view ─────────────────────────────────────────────
  if (receipt) {
    const messageText = buildReceiptMessage(receipt, receiptLang);
    const hasPhone = !!(receipt.client_phone || "").trim();

    async function copyText() {
      try {
        await navigator.clipboard.writeText(messageText);
        toast.success("Receipt text copied to clipboard");
      } catch {
        toast.error("Copy failed — please select and copy manually");
      }
    }

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="receipt-ready-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-emerald-600" />
              {receipt.is_fully_paid ? "Payment recorded — send receipt" : "Deposit recorded — notify client"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Received{" "}
              <span className="font-bold mono text-emerald-700">
                {Number(receipt.amount_paid || 0).toFixed(2)} {receipt.currency}
              </span>
              {!receipt.is_fully_paid && (
                <>
                  {" · Remaining "}
                  <span className="font-bold mono text-amber-700">
                    {Number(receipt.remaining || 0).toFixed(2)} {receipt.currency}
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Language:</span>
              <button
                type="button"
                data-testid="receipt-lang-en"
                onClick={() => setReceiptLang("en")}
                className={`text-xs px-2 py-1 rounded-md ring-1 ring-inset ${
                  receiptLang === "en"
                    ? "bg-primary text-primary-foreground ring-primary"
                    : "bg-white text-muted-foreground ring-border hover:ring-primary/40"
                }`}
              >English</button>
              <button
                type="button"
                data-testid="receipt-lang-tt"
                onClick={() => setReceiptLang("tt")}
                className={`text-xs px-2 py-1 rounded-md ring-1 ring-inset ${
                  receiptLang === "tt"
                    ? "bg-primary text-primary-foreground ring-primary"
                    : "bg-white text-muted-foreground ring-border hover:ring-primary/40"
                }`}
              >Tetum</button>
            </div>

            <Textarea
              data-testid="receipt-message-preview"
              readOnly
              rows={10}
              value={messageText}
              className="text-xs mono bg-slate-50"
            />

            {!hasPhone && (
              <p className="text-[11px] text-amber-800 bg-amber-50 ring-1 ring-amber-200 rounded-md px-2 py-1.5">
                No client phone on file — WhatsApp will open without a recipient. Use &quot;Copy text&quot; and paste into your chat.
              </p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="receipt-close-btn"
            >
              <X className="size-4 mr-1.5" /> Done
            </Button>
            <Button
              variant="outline"
              onClick={copyText}
              data-testid="receipt-copy-btn"
            >
              <Copy className="size-4 mr-1.5" /> Copy text
            </Button>
            <Button
              onClick={() => openWaForReceipt(receipt, receiptLang)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="receipt-send-wa-btn"
            >
              <MessageCircle className="size-4 mr-1.5" /> Send via WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ─── Payment entry view (original) ──────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="payment-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="size-4 text-primary" /> Record Payment
          </DialogTitle>
          <DialogDescription className="text-xs">
            Outstanding balance:{" "}
            <span className="font-bold text-primary mono">
              {balance.toFixed(2)} {currency}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Amount ({currency})</Label>
              <Input
                data-testid="payment-amount"
                type="number" step="0.01" min="0" max={balance}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <div className="flex flex-wrap gap-3 mt-1" data-testid="payment-quick-strip">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                  Deposit:
                </span>
                <button
                  type="button"
                  data-testid="payment-quick-25"
                  className="text-[10px] font-semibold text-primary hover:underline"
                  onClick={() => setAmount((balance * 0.25).toFixed(2))}
                >
                  25%
                </button>
                <button
                  type="button"
                  data-testid="payment-quick-50"
                  className="text-[10px] font-semibold text-primary hover:underline"
                  onClick={() => setAmount((balance * 0.5).toFixed(2))}
                >
                  50%
                </button>
                <button
                  type="button"
                  data-testid="payment-quick-75"
                  className="text-[10px] font-semibold text-primary hover:underline"
                  onClick={() => setAmount((balance * 0.75).toFixed(2))}
                >
                  75%
                </button>
                <button
                  type="button"
                  data-testid="payment-quick-full"
                  className="text-[10px] font-semibold text-emerald-700 hover:underline"
                  onClick={() => setAmount(balance.toFixed(2))}
                >
                  Full
                </button>
              </div>
            </div>
            <div>
              <Label className="overline">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {METHODS.map((m) => (
                    <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Paid on</Label>
              <Input
                data-testid="payment-date"
                type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)}
              />
            </div>
            <div>
              <Label className="overline">Reference</Label>
              <Input
                data-testid="payment-reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Bank txn ID / Cheque #"
              />
            </div>
          </div>
          <div>
            <Label className="overline">Notes</Label>
            <Textarea
              data-testid="payment-notes"
              rows={2} value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes (optional)"
            />
          </div>

          {isPartial && (
            <div
              data-testid="payment-partial-hint"
              className="flex items-center justify-between p-2.5 rounded-md bg-amber-50 ring-1 ring-amber-200 text-xs"
            >
              <span className="text-amber-900">
                <span className="font-semibold">Partial / deposit payment.</span>{" "}
                Remaining balance after this payment:
              </span>
              <span className="mono font-bold text-amber-900">
                {remaining.toFixed(2)} {currency}
              </span>
            </div>
          )}

          {willSettle && (
            <label className="flex items-center gap-2 text-xs select-none p-2 rounded-md bg-emerald-50 ring-1 ring-emerald-200">
              <input
                data-testid="payment-auto-email"
                type="checkbox"
                checked={autoEmail}
                onChange={(e) => setAutoEmail(e.target.checked)}
                className="accent-emerald-600"
              />
              <span>
                <span className="font-semibold text-emerald-700">Auto-email paid receipt</span>{" "}
                to {invoice?.client_snapshot?.email || "client"} after marking paid
              </span>
            </label>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}
                  data-testid="payment-cancel">
            <X className="size-4 mr-1.5" /> Cancel
          </Button>
          <Button onClick={submit} disabled={busy || numericAmount <= 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="payment-submit">
            <Save className="size-4 mr-1.5" /> {busy ? "Saving…" : "Record payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
